import { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  Fade,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  CloudDownload as DownloadIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import AutoCompleteDropdown from "../components/primitives/AutoCompleteDropdown";
import Button from "../components/primitives/Button";
import { getConfigs, getUploads } from "../api/CsvUpload";
import { useUser } from "../context/UserContext";
import { useAlert } from "../hook/useAlert";

const UploadGovernance = () => {
  const { user } = useUser();
  const { onError, onInfo } = useAlert();

  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState("");
  const [uploads, setUploads] = useState([]);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
  const [isLoadingUploads, setIsLoadingUploads] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const paginatedUploads = uploads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleConfigSelect = (e) => {
    setSelectedConfig(e.target.value);
  };

  const handleRefresh = async () => {
    if (selectedConfig) {
      setIsLoadingUploads(true);
      try {
        const uploadsData = await getUploads(selectedConfig);
        setUploads(uploadsData || []);
      } catch (error) {
        onError("Failed to refresh uploads");
      } finally {
        setIsLoadingUploads(false);
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      COMPLETED: { color: "success", label: "Completed" },
      PROCESSING: { color: "warning", label: "Processing" },
      FAILED: { color: "error", label: "Failed" },
      PENDING: { color: "default", label: "Pending" },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleExport = () => {
    const csv = [
      ["Partner", "Filename", "Status", "Uploaded At", "Uploaded By", "Uploaded By Name"],
      ...uploads.map((u) => [
        u.configId,
        u.filename,
        u.status,
        u.uploadedAt,
        u.uploadedBy,
        u.uploadedByName,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `uploads-${selectedConfig}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const loadConfigs = async () => {
      if (!user?.region) return;

      setIsLoadingConfigs(true);
      try {
        const configsData = await getConfigs(user.region);
        setConfigs(configsData);
        if (configsData.length === 0) {
          onInfo("No configurations available");
        }
      } catch (error) {
        onError("Failed to load configurations");
      } finally {
        setIsLoadingConfigs(false);
      }
    };
    setSelectedConfig("");
    loadConfigs();
  }, [user?.region]);

  useEffect(() => {
    const loadUploads = async () => {
      if (!selectedConfig) {
        setUploads([]);
        return;
      }

      setIsLoadingUploads(true);
      try {
        const uploadsData = await getUploads(selectedConfig);
        setUploads(uploadsData || []);
        setPage(0);
        if (!uploadsData || uploadsData.length === 0) {
          onInfo("No uploads found for this configuration");
        }
      } catch (error) {
        onError("Failed to load uploads");
        setUploads([]);
      } finally {
        setIsLoadingUploads(false);
      }
    };

    loadUploads();
  }, [selectedConfig]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{ p: 4 }}
        className="animate__animated animate__fadeIn animate__delay-05s"
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 4, gap: 2 }}>
          <AssessmentIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">Upload Governance</Typography>
        </Box>
        <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
          <AutoCompleteDropdown
            label="Select Configuration"
            id="config-select"
            name="config"
            value={selectedConfig}
            onChange={handleConfigSelect}
            options={
              configs?.map((config) => ({
                value: config.id,
                label: config.id,
              })) || []
            }
            searchPlaceholder="Search configurations..."
            disabled={isLoadingConfigs}
          />
        </Card>
        {selectedConfig && (
          <Fade in timeout={300}>
            <Card variant="outlined" sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography variant="h6">Uploads for {selectedConfig}</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Refresh">
                    <IconButton onClick={handleRefresh} disabled={isLoadingUploads} color="primary">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    disabled={uploads.length === 0}
                    name="Export"
                    onClick={handleExport}
                  />
                </Box>
              </Box>
              {isLoadingUploads ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : uploads.length === 0 ? (
                <Alert severity="info" sx={{ my: 3 }}>
                  No uploads found for this configuration.
                </Alert>
              ) : (
                <>
                  <TableContainer
                    sx={{ maxHeight: 500, border: "1px solid #e0e0e0", borderRadius: 1 }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                            Filename
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                            Status
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                            Uploaded At
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                            Uploaded By
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                            Uploader Name
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedUploads.map((upload, index) => (
                          <TableRow
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${upload.configId}-${upload.uploadedAt}-${index}`}
                            hover
                            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {upload.filename}
                              </Typography>
                            </TableCell>
                            <TableCell>{getStatusChip(upload.status)}</TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(upload.uploadedAt)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {upload.uploadedBy}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{upload.uploadedByName}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={uploads.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ borderTop: "1px solid #e0e0e0" }}
                  />

                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total uploads: {uploads.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last date of ingestion: {formatDate(uploads[0]?.uploadedAt)}
                    </Typography>
                  </Box>
                </>
              )}
            </Card>
          </Fade>
        )}
      </Paper>
    </Container>
  );
};

export default UploadGovernance;
